import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Badge, Spinner, Form, Toast, ToastContainer } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useUserDietPreferences, useUserCuisinePreferences, useUserAllergenPreferences, useUserMealPlanPreferences } from '../hooks/userPreferencesHelper';
import styles from './UserPreferences.module.css';
import supabase from '../supabaseClient';
import ErrorBoundary from '../components/ErrorBoundary';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import { useUserShowMealDeletionConfirmation } from '../hooks/userPreferencesHelper';


const UserPreferences = () => {
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dietValidationError, setDietValidationError] = useState(null);
  const [cuisineValidationError, setCuisineValidationError] = useState(null);
  const [showDietToast, setShowDietToast] = useState(false);
  const [showCuisineToast, setShowCuisineToast] = useState(false);
  const [allDiets, setAllDiets] = useState([]);
  const [activeDiets, setActiveDiets] = useState([]);
  const [allCuisines, setAllCuisines] = useState([]);
  const [activeCuisines, setActiveCuisines] = useState([]);
  const [allergenPreferences, setAllergenPreferences] = useState([]);
  const [activeAllergens, setActiveAllergens] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const { sessionToken, updatePgUserProperty } = useAuth();
  const [allIngredients, setAllIngredients] = useState([]);
  
  
  // Meal plan preferences state
  const [mealPlanPreferences, setMealPlanPreferences] = useState({
    number_of_breakfast_meals: 0,
    number_of_lunch_meals: 0,
    number_of_dinner_meals: 0,
    breakfast_serving_size: 1,
    lunch_serving_size: 1,
    dinner_serving_size: 1
  });

  const { pgUser } = useAuth();
  const { addUserDiet, deleteUserDiet, error: dietError, loading: dietLoading } = useUserDietPreferences();
  const { addUserCuisine, deleteUserCuisine, error: cuisineError, loading: cuisineLoading } = useUserCuisinePreferences();
  const { addUserAllergen, deleteUserAllergen, error: allergenError, loading: allergenLoading } = useUserAllergenPreferences();
  const { updateUserMealPlanPreferences, error: mealPlanError, loading: mealPlanLoading } = useUserMealPlanPreferences();
  const { updateUserShowMealDeletionConfirmation, error: showMealDeletionConfirmationError, loading: showMealDeletionConfirmationLoading } = useUserShowMealDeletionConfirmation();
  const [settingsObject, setSettingsObject] = useState({
    show_meal_deletion_confirmation_modal: pgUser?.show_meal_deletion_confirmation_modal
  });

  // Debug logging helper
  const logDebug = (action, details) => {
    const timestamp = new Date().toISOString();
    console.debug(`[UserPreferences ${timestamp}] ${action}:`, details);
  };

  // Performance logging helper
  const logTiming = async (operation, fn) => {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      logDebug(`${operation} completed`, { durationMs: duration.toFixed(2) });
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      logDebug(`${operation} failed`, { durationMs: duration.toFixed(2), error });
      throw error;
    }
  };

  // Update settingsObject when pgUser becomes available
  useEffect(() => {
    if (pgUser?.show_meal_deletion_confirmation_modal !== undefined) {
      setSettingsObject({
        show_meal_deletion_confirmation_modal: pgUser.show_meal_deletion_confirmation_modal
      });
    }
  }, [pgUser?.show_meal_deletion_confirmation_modal]);

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!pgUser || !pgUser.id) {
        logDebug("Fetch skipped", { reason: "No pgUser.id available" });
        return;
      }

      try {
        logDebug("Fetch started", { 
          pgUserId: pgUser.id,
          hasSessionToken: !!sessionToken,
          timestamp: Date.now()
        });
        
        // Prefer the token from auth context to avoid extra Supabase calls
        const token = sessionToken;
        if (!token) {
          throw new Error('No valid session found');
        }

        // Lightweight retry for transient auth/service errors
        const fetchWithRetry = async (url, options, retries = 1) => {
          const start = performance.now();
          try {
            const res = await fetchWithCSRF(url, options);
            const duration = performance.now() - start;
            
            logDebug('API call', {
              url,
              status: res.status,
              durationMs: duration.toFixed(2)
            });
            
            if (!res.ok && retries > 0 && [401, 403, 503].includes(res.status)) {
              logDebug('Retrying failed request', {
                url,
                status: res.status,
                retriesLeft: retries - 1
              });
              await new Promise(r => setTimeout(r, 500));
              return fetchWithRetry(url, options, retries - 1);
            }
            return res;
          } catch (error) {
            const duration = performance.now() - start;
            logDebug('API call failed', {
              url,
              error: error?.message,
              durationMs: duration.toFixed(2)
            });
            throw error;
          }
        };

        const [allDietsRes, allCuisinesRes, userDietsRes, userCuisinesRes, allergenRes, userRes, allIngredientsRes] = await Promise.all([
          fetchWithRetry(`/api/v1/diet`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetchWithRetry(`/api/v1/cuisine`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetchWithRetry(`/api/v1/user-diet/user-diets`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetchWithRetry(`/api/v1/user-cuisine/user-cuisines`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetchWithRetry(`/api/v1/user-allergen-ingredient/user-allergens`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetchWithRetry(`/api/v1/users/${pgUser.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetchWithRetry(`/api/v1/ingredient`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        const allDietsData = await allDietsRes.json();
        const allCuisinesData = await allCuisinesRes.json();
        const userDietsData = await userDietsRes.json();
        const userCuisinesData = await userCuisinesRes.json();
        const allergenData = await allergenRes.json();
        const userData = await userRes.json();
        const allIngredientsData = await allIngredientsRes.json();
        setAllDiets(allDietsData);
        setAllCuisines(allCuisinesData);
        setAllergenPreferences(allergenData);

        setAllIngredients(allIngredientsData);
        const userDietIds = userDietsData.map(diet => diet.id);
        setActiveDiets(userDietIds);

        // Check if user has at least one diet selected
        if (userDietIds.length === 0) {
          setDietValidationError("At least one dietary preference must be selected");
        } else {
          setDietValidationError(null);
        }

        const userCuisineIds = userCuisinesData.map(cuisine => cuisine.id);
        setActiveCuisines(userCuisineIds);

        // Check if user has at least three cuisines selected
        if (userCuisineIds.length < 3) {
          setCuisineValidationError("Please select at least three cuisine preferences");
        } else {
          setCuisineValidationError(null);
        }

        // Handle case where allergenData might not be an array
        setActiveAllergens(Array.isArray(allergenData) ? allergenData.map(allergen => allergen.id) : []);

        // Set meal plan preferences from user data
        if (userData && userData.length > 0) {
          const user = userData[0];
          setMealPlanPreferences({
            number_of_breakfast_meals: user.preferred_number_of_breakfast_meals || 0,
            number_of_lunch_meals: user.preferred_number_of_lunch_meals || 0,
            number_of_dinner_meals: user.preferred_number_of_dinner_meals || 0,
            breakfast_serving_size: user.preferred_breakfast_serving_size || 1,
            lunch_serving_size: user.preferred_lunch_serving_size || 1,
            dinner_serving_size: user.preferred_dinner_serving_size || 1
          });
        }

      } catch (err) {
        console.error("Error fetching preferences:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [pgUser]);

  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_POSTGRES_API_ENDPOINT}/ingredient`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setIngredients(data);
        }
      } catch (error) {
        console.error('Error fetching ingredients:', error);
        setError(error.message);
      }
    };

    fetchIngredients();
  }, [sessionToken]);

  useEffect(() => {
    // Filter ingredients based on search query
    if (searchQuery.trim() === '') {
      setSearchResults([]);
    } else {
      const filtered = ingredients.filter(ingredient => 
        ingredient.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered.slice(0, 10)); // Limit to 10 results
    }
  }, [searchQuery, ingredients]);

  const handleDietToggle = async (dietId) => {
    try {
      if (activeDiets.includes(dietId)) {
        // Check if this is the last active diet
        if (activeDiets.length === 1) {
          setDietValidationError("At least one dietary preference must be selected");
          setShowDietToast(true);
          return; // Prevent deselection
        }
        // If not the last diet, remove it
        await deleteUserDiet(dietId);
        setActiveDiets(prev => prev.filter(id => id !== dietId));
      } else {
        // If adding a diet, clear any validation error
        setDietValidationError(null);
        setShowDietToast(false);
        // Add the diet
        await addUserDiet(dietId);
        setActiveDiets(prev => [...prev, dietId]);
      }
    } catch (err) {
      console.error("Error toggling diet preference:", err);
      setError(err.message);
    }
  };

  const handleCuisineToggle = async (cuisineId) => {
    try {
      if (activeCuisines.includes(cuisineId)) {
        // Check if removing this cuisine would go below minimum
        if (activeCuisines.length <= 3) {
          setCuisineValidationError("Please maintain at least three cuisine preferences");
          setShowCuisineToast(true);
          return; // Prevent deselection
        }
        // If not going below minimum, remove it
        await deleteUserCuisine(cuisineId);
        setActiveCuisines(prev => prev.filter(id => id !== cuisineId));
      } else {
        // If adding a cuisine, clear validation error if we're reaching minimum
        if (activeCuisines.length >= 2) { // Will be 3 after adding
          setCuisineValidationError(null);
          setShowCuisineToast(false);
        }
        // Add the cuisine
        await addUserCuisine(cuisineId);
        setActiveCuisines(prev => [...prev, cuisineId]);
      }
    } catch (err) {
      console.error("Error toggling cuisine preference:", err);
      setError(err.message);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectAllergen = async (ingredient) => {
    try {
      // Add the selected allergen if it's not already selected
      if (!activeAllergens.includes(ingredient.id)) {
        await addUserAllergen(ingredient.id);
        setActiveAllergens(prev => [...prev, ingredient.id]);
      }
      // setSearchQuery('');
    } catch (err) {
      console.error("Error adding allergen:", err);
      setError(err.message);
    }
  };

  const handleRemoveAllergen = async (ingredientId) => {
    try {
      await deleteUserAllergen(ingredientId);
      setActiveAllergens(prev => prev.filter(id => id !== ingredientId));
    } catch (err) {
      console.error("Error removing allergen:", err);
      setError(err.message);
    }
  };

  const handleMealPlanPreferencesChange = (field, value) => {
    const updatedPreferences = {
      ...mealPlanPreferences,
      [field]: parseInt(value) || 0
    };
    
    setMealPlanPreferences(updatedPreferences);
    return updatedPreferences; // Return the updated preferences
  };

  const handleMealPlanPreferencesBlur = async (preferencesToUpdate = null) => {
    try {
      // Use the passed preferences or fall back to current state
      const preferencesToSend = preferencesToUpdate || mealPlanPreferences;
      
      // Create a promise that resolves after 1 second
      const minLoadingTime = new Promise(resolve => setTimeout(resolve, 4000));
      
      // Update the preferences in the backend
      const updatePromise = updateUserMealPlanPreferences(pgUser.id, preferencesToSend);
      
      // Wait for both the API call and the minimum loading time
      await Promise.all([updatePromise, minLoadingTime]);
    } catch (err) {
      console.error("Error updating meal plan preferences:", err);
      setError(err.message);
    }
  };

  const handleIncrement = (field, max) => {
    const currentValue = mealPlanPreferences[field];
    if (currentValue < max) {
      const updatedPreferences = {
        ...mealPlanPreferences,
        [field]: currentValue + 1
      };
      setMealPlanPreferences(updatedPreferences);
      // Update backend immediately
      updateUserMealPlanPreferences(pgUser.id, updatedPreferences);
    }
  };

  const handleDecrement = (field, min) => {
    const currentValue = mealPlanPreferences[field];
    if (currentValue > min) {
      const updatedPreferences = {
        ...mealPlanPreferences,
        [field]: currentValue - 1
      };
      setMealPlanPreferences(updatedPreferences);
      // Update backend immediately
      updateUserMealPlanPreferences(pgUser.id, updatedPreferences);
    }
  };

  // Get ingredient names for display
  const getIngredientName = (id) => {
    console.log('looking for ingredient with id: ', id, ' out of ingredients: ', ingredients);
    const ingredient = ingredients.find(ing => ing.id === id);
    console.log('ingredient found: ', ingredient);
    return ingredient ? ingredient.name : `Ingredient ${id}`;
  };

  // Handle auth loading state
  useEffect(() => {
    if (pgUser === null) {
      setIsAuthLoading(false);
      return;
    }
    setIsAuthLoading(false);
  }, [pgUser]);

  return (
    <ErrorBoundary fallbackMessage="Unable to load user preferences. Please try again later.">
      <Container className="py-3">
        {isAuthLoading ? (
          <div className="text-center my-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : !pgUser ? (
          <div className="alert alert-warning">
            <p className="h5 mb-3">Unable to connect to the server. Please try again later or send a message to <a href="mailto:support@getautochef.com">support@getautochef.com</a></p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading preferences...</span>
            </Spinner>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            <p className="h5 mb-3">Error loading preferences. Please try again later or contact support@getautochef.com </p>
            <Button variant="danger" onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : (
          <>
            <ToastContainer 
              className="p-3" 
              style={{ 
                position: 'fixed',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 9999,
                width: '50%'
              }}
            >
              <Toast 
                show={showDietToast} 
                onClose={() => setShowDietToast(false)} 
                delay={3000} 
                autohide
                style={{
                  backgroundColor: '#52cd92',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  width: '100%'
                }}
              >
                <Toast.Body style={{ color: 'white', textAlign: 'center', backgroundColor: '#dc3545', borderRadius: '12px' }}>{dietValidationError}</Toast.Body>
              </Toast>

              <Toast 
                show={showCuisineToast} 
                onClose={() => setShowCuisineToast(false)} 
                delay={3000} 
                autohide
                style={{
                  backgroundColor: '#52cd92',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  width: '100%',
                  marginTop: '10px'
                }}
              >

                <Toast.Body style={{ color: 'white', textAlign: 'center', backgroundColor: '#dc3545', borderRadius: '12px' }}>{cuisineValidationError}</Toast.Body>
              </Toast>
            </ToastContainer>

            {/* <h1 className="mb-4">Your Preferences</h1> */}

            <Row className="mb-4">
              <Col>
                <h2 className={styles.preferencesSectionTitle}>Dietary Preferences</h2>
                <p className="text-muted">Your auto-generated meals will pertain to at least one of your selected diets</p>
                {/* Remove the inline error messages since we're using toasts now */}
                <div className={styles.dietOptionsGrid}>
                  {allDiets.map((diet, index) => (
                    <button
                      key={`diet-${diet.id}-${index}`}
                      type="button"
                      className={`btn setup-preference-option ${activeDiets.includes(diet.id) ? 'active' : ''}`}
                      onClick={() => handleDietToggle(diet.id)}
                      disabled={dietLoading}
                    >
                      <div className="preference-option-content">
                        <span className="diet-name">{diet.name}</span>
                        <div className="diet-icon-wrapper">
                          <img 
                            src={`/icons/dietOptions/${diet.name.replace(/\s+/g, '')}.jpg`}
                            alt={`${diet.name} icon`}
                            className="diet-icon"
                            onError={(e) => {
                              // Prevent infinite loop by removing the error handler after first try
                              e.target.onError = null;
                              // Use a data URI for the fallback to avoid additional network requests
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzIDEwIDQgMTciPjwvcG9seWxpbmU+PC9zdmc+';
                            }}
                            loading="eager"
                          />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <h2 className={styles.preferencesSectionTitle}>Cuisine Preferences</h2>
                <p className="text-muted">Your auto-generated meals will pertain to at least one of your selected cuisines (minimum 3 selections required)</p>
                {/* Remove the inline error messages since we're using toasts now */}
                <div className={styles.dietOptionsGrid}>
                  {allCuisines.map((cuisine, index) => (
                    <button
                      key={`cuisine-${cuisine.id}-${index}`}
                      type="button"
                      className={`btn setup-preference-option ${activeCuisines.includes(cuisine.id) ? 'active' : ''}`}
                      onClick={() => handleCuisineToggle(cuisine.id)}
                      disabled={cuisineLoading}
                    >
                      <div className="preference-option-content">
                        <span className="diet-name">{cuisine.name}</span>
                        <div className="diet-icon-wrapper">
                          <img 
                            src={`/icons/cuisineOptions/${cuisine.name.replace(/\s+/g, '')}.svg`}
                            alt={`${cuisine.name} icon`}
                            className="diet-icon"
                            onError={(e) => {
                              // Prevent infinite loop by removing the error handler after first try
                              e.target.onError = null;
                              // Use a data URI for the fallback to avoid additional network requests
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzIDEwIDQgMTciPjwvcG9seWxpbmU+PC9zdmc+';
                            }}
                            loading="eager"
                          />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <h2 className={styles.preferencesSectionTitle}>Ingredients to Avoid</h2>
                {allergenError && <div className="text-danger mb-2">{allergenError}</div>}
                
                <div className="search-container mb-3">
                  <input
                    type="text"
                    className="form-control-custom"
                    placeholder="Search for ingredients..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ width: '50%' }}
                  />
                </div>
                
                {searchQuery && searchResults.length > 0 && (
                  <div className="search-results-container mb-4">
                    <div className="search-results-buttons d-flex flex-wrap gap-2">
                      {searchResults.map(ingredient => (
                        <button
                          key={ingredient.id}
                          type="button"
                          className="btn-outline-black"
                          onClick={() => {
                            handleSelectAllergen(ingredient);
                            setSearchQuery(''); // Clear search after selection
                          }}
                          disabled={allergenLoading || activeAllergens.includes(ingredient.id)}
                        >
                          {ingredient.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {searchQuery && searchResults.length === 0 && (
                  <div className="text-muted mb-4">
                    No ingredients found matching your search
                  </div>
                )}
                
                <div className="selected-allergens mt-4">
                  {activeAllergens.length > 0 && <h6 className="text-muted">You won't see meals with these ingredients</h6>}
                  {activeAllergens.length === 0 ? (
                    <p className="text-muted">No ingredients selected</p>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {activeAllergens.map(allergenId => (
                        <div key={allergenId} className="badge d-flex align-items-center p-2 selected-allergen-ingredient-badge">
                          <img 
                            src={getIngredientImageUrl(allergenId)}
                            alt={`${allergenId} icon`}
                            className=""
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                            style={{ width: '25px', height: '25px', marginRight: '5px' }}
                          />
                          <span>{getIngredientName(allergenId)}</span>
                          <button 
                            type="button" 
                            className="btn-close btn-close-white ms-2" 
                            style={{ fontSize: '0.5rem' }}
                            onClick={() => handleRemoveAllergen(allergenId)}
                            disabled={allergenLoading}
                            aria-label="Remove"
                          ></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <h2 className={styles.preferencesSectionTitle}>Meal Plan Preferences</h2>
                <p className="text-muted">Configure how many meals and serving sizes you'd like for each meal type</p>
                {mealPlanError && <div className="text-danger mb-2">{mealPlanError}</div>}

                <div className={styles.mealPlanCardsRow}>
                  {/* <div className={styles.mealPlanCard}>
                    <div className={styles.mealPlanTitle}><span className={styles.mealPlanIcon}>🍳</span>Breakfast</div>
                    <div className={styles.mealPlanLabel}>Meals per week</div>
                    <div className={styles.segmentedControl}>
                      {[0,1,2,3,4,5,6,7].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            styles.segmentedOption +
                            (mealPlanPreferences.number_of_breakfast_meals === value ? ' ' + styles.segmentedOptionSelected : '')
                          }
                          onClick={() => {
                            const updatedPrefs = handleMealPlanPreferencesChange('number_of_breakfast_meals', value);
                            handleMealPlanPreferencesBlur(updatedPrefs);
                          }}
                          disabled={mealPlanLoading}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                    <div className={styles.mealPlanLabel}>Serving size</div>
                    <div className={styles.segmentedControl}>
                      {[1,2,3,4,5,6,7,8].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            styles.segmentedOption +
                            (mealPlanPreferences.breakfast_serving_size === value ? ' ' + styles.segmentedOptionSelected : '')
                          }
                          onClick={() => {
                            const updatedPrefs = handleMealPlanPreferencesChange('breakfast_serving_size', value);
                            handleMealPlanPreferencesBlur(updatedPrefs);
                          }}
                          disabled={mealPlanLoading}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div> */}

                  {/* <div className={styles.mealPlanCard}>
                    <div className={styles.mealPlanTitle}><span className={styles.mealPlanIcon}>🥪</span>Lunch</div>
                    <div className={styles.mealPlanLabel}>Meals per week</div>
                    <div className={styles.segmentedControl}>
                      {[0,1,2,3,4,5,6,7].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            styles.segmentedOption +
                            (mealPlanPreferences.number_of_lunch_meals === value ? ' ' + styles.segmentedOptionSelected : '')
                          }
                          onClick={() => {
                            const updatedPrefs = handleMealPlanPreferencesChange('number_of_lunch_meals', value);
                            handleMealPlanPreferencesBlur(updatedPrefs);
                          }}
                          disabled={mealPlanLoading}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                    <div className={styles.mealPlanLabel}>Serving size</div>
                    <div className={styles.segmentedControl}>
                      {[1,2,3,4,5,6,7,8].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            styles.segmentedOption +
                            (mealPlanPreferences.lunch_serving_size === value ? ' ' + styles.segmentedOptionSelected : '')
                          }
                          onClick={() => {
                            const updatedPrefs = handleMealPlanPreferencesChange('lunch_serving_size', value);
                            handleMealPlanPreferencesBlur(updatedPrefs);
                          }}
                          disabled={mealPlanLoading}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div> */}

                  <div className={styles.mealPlanCard}>
                    {/* <div className={styles.mealPlanTitle}><span className={styles.mealPlanIcon}>🍽️</span>Dinner</div> */}
                    <div className={styles.mealPlanLabel}>Meals per week</div>
                    <div className={`${styles.segmentedControl} ${styles.segmentedControl7}`}>
                      {[1,2,3,4,5,6,7].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            styles.segmentedOption +
                            (mealPlanPreferences.number_of_dinner_meals === value ? ' ' + styles.segmentedOptionSelected : '')
                          }
                          onClick={() => {
                            const updatedPrefs = handleMealPlanPreferencesChange('number_of_dinner_meals', value);
                            handleMealPlanPreferencesBlur(updatedPrefs);
                          }}
                          disabled={mealPlanLoading}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                    <div className={styles.mealPlanLabel}>Serving size</div>
                    <div className={styles.segmentedControl}>
                      {[1,2,3,4,5,6,7,8].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            styles.segmentedOption +
                            (mealPlanPreferences.dinner_serving_size === value ? ' ' + styles.segmentedOptionSelected : '')
                          }
                          onClick={() => {
                            const updatedPrefs = handleMealPlanPreferencesChange('dinner_serving_size', value);
                            handleMealPlanPreferencesBlur(updatedPrefs);
                          }}
                          disabled={mealPlanLoading}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* {mealPlanLoading && (
                  <div className="text-center mt-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Saving preferences...</span>
                  </div>
                )} */}
              </Col>
            </Row>
            <Row className="mb-4">
              <Col>
                <h2 className={styles.preferencesSectionTitle}>Settings</h2>
                <div style={{ textAlign: 'left' }}>
                  <Form.Check
                    type="checkbox"
                    label="Ask before meal deletion"
                    checked={settingsObject.show_meal_deletion_confirmation_modal}
                    className="custom-checkbox pl-1"
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      // Update local state immediately for responsive UI
                      setSettingsObject(prev => ({
                        ...prev,
                        show_meal_deletion_confirmation_modal: newValue
                      }));
                      // Update global pgUser so all components get the new value
                      updatePgUserProperty('show_meal_deletion_confirmation_modal', newValue);
                      // Update backend
                      updateUserShowMealDeletionConfirmation(newValue);
                    }}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </ErrorBoundary>
  );
};

export default UserPreferences;
